import { useState, useCallback, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { savePrescription } from '../../services/api/apis';

const DRAFT_KEY_PREFIX = 'rx_draft_';

export interface PrescriptionFormData {
  drName: string;
  regNo: string;
  specialization: string;
  patientName: string;
  docId: string;
  ageGender: string;
  issueDate: string;
  signature: string;
  complaintsOf: string;
  historyOfSymptoms: string;
  pastHistoryOfSimilarSymptoms: 'Yes' | 'No';
  pastHistoryDetails: string;
  concurrentIllnesses: string;
  substanceIntake: 'Yes' | 'No';
  substanceIntakeDetails: string;
  significantPastHistory: string;
  historyOfSurgery: 'Yes' | 'No';
  surgeryDetails: string;
  historyOfTravel: 'Yes' | 'No';
  travelDetails: string;
  relevantFamilyHistory: 'Yes' | 'No';
  familyHistoryDetails: string;
  intensityOfSymptoms: 'Mild' | 'Moderate' | 'Severe';
  selfMedicationHomeRemedies: 'Yes' | 'No';
  selfMedicationDetails: string;
  fullDiagnosis: string;
  rxLines: string[];
  adviceByRMP: string;
  specialNotesByRMP: string;
}

const defaultData: PrescriptionFormData = {
  drName: '',
  regNo: '',
  specialization: '',
  patientName: '',
  docId: '',
  ageGender: '',
  issueDate: new Date().toISOString().split('T')[0],
  signature: '',
  complaintsOf: '',
  historyOfSymptoms: '',
  pastHistoryOfSimilarSymptoms: 'No',
  pastHistoryDetails: '',
  concurrentIllnesses: '',
  substanceIntake: 'No',
  substanceIntakeDetails: '',
  significantPastHistory: '',
  historyOfSurgery: 'No',
  surgeryDetails: '',
  historyOfTravel: 'No',
  travelDetails: '',
  relevantFamilyHistory: 'No',
  familyHistoryDetails: '',
  intensityOfSymptoms: 'Mild',
  selfMedicationHomeRemedies: 'No',
  selfMedicationDetails: '',
  fullDiagnosis: '',
  rxLines: [''],
  adviceByRMP: '',
  specialNotesByRMP: '',
};

export interface UsePrescriptionFormOptions {
  patientDocId?: string;
  initialData?: Partial<PrescriptionFormData>;
  submittedData?: Partial<PrescriptionFormData>;
  persistDraft?: boolean;
  onSubmitSuccess?: () => void;
}

export interface UsePrescriptionFormReturn {
  formData: PrescriptionFormData;
  isDirty: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  setField: <K extends keyof PrescriptionFormData>(field: K, value: PrescriptionFormData[K]) => void;
  setRxLine: (index: number, value: string) => void;
  addRxLine: () => void;
  removeRxLine: (index: number) => void;
  reset: () => void;
  submit: () => Promise<void>;
}

export function usePrescriptionForm(options: UsePrescriptionFormOptions = {}): UsePrescriptionFormReturn {
  const { patientDocId, initialData, submittedData, persistDraft = false, onSubmitSuccess } = options;
  const draftKey = `${DRAFT_KEY_PREFIX}${patientDocId ?? 'new'}`;

  const buildInitial = useCallback((): PrescriptionFormData => {
    // Doctor-submitted data takes priority over any local draft
    if (submittedData && Object.keys(submittedData).length > 0) {
      return { ...defaultData, ...initialData, ...submittedData };
    }
    if (persistDraft && patientDocId) {
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) return { ...defaultData, ...JSON.parse(saved) };
      } catch {}
    }
    return { ...defaultData, ...initialData };
  }, [draftKey, initialData, submittedData, patientDocId, persistDraft]);

  const [formData, setFormData] = useState<PrescriptionFormData>(buildInitial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const baselineRef = useRef(JSON.stringify(buildInitial()));
  const isDirty = JSON.stringify(formData) !== baselineRef.current;

  useEffect(() => {
    if (!persistDraft) return;
    try { localStorage.setItem(draftKey, JSON.stringify(formData)); } catch {}
  }, [formData, draftKey, persistDraft]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const setField = useCallback(
    <K extends keyof PrescriptionFormData>(field: K, value: PrescriptionFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const setRxLine = useCallback((index: number, value: string) => {
    setFormData((prev) => {
      const lines = [...prev.rxLines];
      lines[index] = value;
      return { ...prev, rxLines: lines };
    });
  }, []);

  const addRxLine = useCallback(() => {
    setFormData((prev) => ({ ...prev, rxLines: [...prev.rxLines, ''] }));
  }, []);

  const removeRxLine = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      rxLines: prev.rxLines.length > 1 ? prev.rxLines.filter((_, i) => i !== index) : [''],
    }));
  }, []);

  const reset = useCallback(() => {
    const fresh = { ...defaultData, ...initialData };
    setFormData(fresh);
    setSubmitError(null);
    baselineRef.current = JSON.stringify(fresh);
    if (persistDraft) {
      try { localStorage.removeItem(draftKey); } catch {}
    }
  }, [draftKey, initialData, persistDraft]);

  const submit = useCallback(async () => {
    if (!formData.complaintsOf.trim()) {
      setSubmitError('Chief complaints (C/O) are required');
      return;
    }
    if (!formData.fullDiagnosis.trim()) {
      setSubmitError('Full diagnosis is required');
      return;
    }
    if (!patientDocId) {
      setSubmitError('No patient selected');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Save the full prescription form to its own PRESCRIPTION item so
      // every field is persisted and can be reloaded and edited.
      await savePrescription(patientDocId, {
        drName:                       formData.drName,
        specialization:               formData.specialization,
        regNo:                        formData.regNo,
        patientName:                  formData.patientName,
        docId:                        patientDocId,
        ageGender:                    formData.ageGender,
        issueDate:                    formData.issueDate,
        complaintsOf:                 formData.complaintsOf,
        historyOfSymptoms:            formData.historyOfSymptoms,
        pastHistoryOfSimilarSymptoms: formData.pastHistoryOfSimilarSymptoms,
        concurrentIllnesses:          formData.concurrentIllnesses,
        substanceIntake:              formData.substanceIntake,
        substanceIntakeDetails:       formData.substanceIntakeDetails || undefined,
        significantPastHistory:       formData.significantPastHistory,
        historyOfSurgery:             formData.historyOfSurgery,
        surgeryDetails:               formData.surgeryDetails || undefined,
        historyOfTravel:              formData.historyOfTravel,
        travelDetails:                formData.travelDetails || undefined,
        relevantFamilyHistory:        formData.relevantFamilyHistory,
        familyHistoryDetails:         formData.familyHistoryDetails || undefined,
        intensityOfSymptoms:          formData.intensityOfSymptoms,
        selfMedicationHomeRemedies:   formData.selfMedicationHomeRemedies,
        selfMedicationDetails:        formData.selfMedicationDetails || undefined,
        fullDiagnosis:                formData.fullDiagnosis,
        rxLines:                      formData.rxLines.filter(Boolean),
        adviceByRMP:                  formData.adviceByRMP,
        specialNotesByRMP:            formData.specialNotesByRMP,
      });

      baselineRef.current = JSON.stringify(formData);
      if (persistDraft) {
        try { localStorage.removeItem(draftKey); } catch {}
      }
      onSubmitSuccess?.();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [patientDocId, formData, draftKey, persistDraft, onSubmitSuccess]);

  return {
    formData,
    isDirty,
    isSubmitting,
    submitError,
    handleChange,
    setField,
    setRxLine,
    addRxLine,
    removeRxLine,
    reset,
    submit,
  };
}
